import React, {
  useRef,
  useState,
  Dispatch,
  RefObject,
  forwardRef,
  SetStateAction,
  useImperativeHandle,
  ForwardRefRenderFunction,
} from 'react';
import { Grid } from '@mui/material';

import ConsecutiveValidation, {
  ConsecutiveValidationRef,
} from './ConsecutiveValidation';
import BreachingPeriodsChecks, {
  BreachingPeriodsChecksRef,
} from './BreachingPeriodsChecks';
import { InstancesCreationCriteriaError } from '../CreateForm';

import {
  AlarmMetadata,
  AlarmDefinition,
  DataSourceAlarmMetadata,
} from 'types/types';

interface Props {
  alarmMetaData: AlarmMetadata | undefined;
  alarmDefinitionData: AlarmDefinition | null;
  dataSourceMetadataForAlarm: DataSourceAlarmMetadata | undefined;
}

export interface InstancesCreationCriteriaRef {
  validationErrors: InstancesCreationCriteriaError;
  consecutiveBreach: RefObject<ConsecutiveValidationRef>;
  breachingPeriodsChecks: RefObject<BreachingPeriodsChecksRef>;
  instancesValidation: Dispatch<SetStateAction<InstancesCreationCriteriaError>>;
}

const InstancesCreationCriteria: ForwardRefRenderFunction<
  InstancesCreationCriteriaRef,
  Props
> = (
  { alarmMetaData, dataSourceMetadataForAlarm, alarmDefinitionData },
  ref
) => {
  const breachingPeriodsChecksRef = useRef<BreachingPeriodsChecksRef>(null);
  const initialErrors: InstancesCreationCriteriaError = {
    consecutivePeriod: '',
    breachedIntervals: '',
    latestInterval: '',
  };
  const consecutiveValidationRef = useRef<ConsecutiveValidationRef>(null);

  const validationMessages = {
    breachGreaterThanLatest:
      'Breaching interval must be less than or equal to the latest interval',
    latestSmallerThanBreach:
      'Latest interval must be greater than or equal to the breaching interval',
    lessThanZero: 'Interval must be greater than 0',
    maxRetention: 'Latest interval is greater than max retention number',
    smallerThanConsecutivePeriod:
      'Interval must be greater than or equal to the consecutive period',
  };

  const [validationErrors, setValidationErrors] =
    useState<InstancesCreationCriteriaError>(initialErrors);

  const getLatestIntervalValidationMessage = (event) => {
    switch (true) {
      case event === '':
        return '';

      case breachingPeriodsChecksRef.current?.breachNumber?.current?.value &&
        +event < +breachingPeriodsChecksRef.current?.breachNumber.current.value:
        return validationMessages.latestSmallerThanBreach;

      case +event < 0:
        return validationMessages.lessThanZero;

      case breachingPeriodsChecksRef.current?.maxRetentionNumber &&
        breachingPeriodsChecksRef.current?.maxRetentionNumber < +event:
        return validationMessages.maxRetention;

      case validationErrors.latestInterval !== '':
        return '';

      default:
        return '';
    }
  };

  const getBreachingIntervalValidationMessage = (event) => {
    switch (true) {
      case event === '':
        return '';

      case +event < 0:
        return validationMessages.lessThanZero;

      case consecutiveValidationRef.current?.consecutiveBreach &&
        +event <
          consecutiveValidationRef.current.consecutivePeriod.current?.value:
        return validationMessages.smallerThanConsecutivePeriod;

      case breachingPeriodsChecksRef.current?.latestIntervalNumber?.current
        ?.value &&
        +event >
          +breachingPeriodsChecksRef.current.latestIntervalNumber.current.value:
        return validationMessages.breachGreaterThanLatest;

      case validationErrors.breachedIntervals !== '':
        return '';

      default:
        return '';
    }
  };

  const handleOnChange = (event: string, field: string) => {
    let validationMessage: string = '';
    switch (field.toUpperCase()) {
      case 'CONSECUTIVEPERIOD':
        if (
          breachingPeriodsChecksRef.current?.breachingSelected &&
          breachingPeriodsChecksRef.current.breachNumber.current?.value &&
          +event >
            +breachingPeriodsChecksRef.current.breachNumber.current?.value
        ) {
          setValidationErrors((prev) => {
            return {
              ...prev,
              breachedIntervals:
                validationMessages.smallerThanConsecutivePeriod,
            };
          });
        }
        if (
          breachingPeriodsChecksRef.current?.breachingSelected &&
          breachingPeriodsChecksRef.current.breachNumber.current?.value &&
          +event <=
            +breachingPeriodsChecksRef.current.breachNumber.current?.value &&
          validationErrors.breachedIntervals ===
            validationMessages.smallerThanConsecutivePeriod
        ) {
          setValidationErrors((prev) => {
            return {
              ...prev,
              breachedIntervals: '',
            };
          });
        }
        if (validationErrors.consecutivePeriod !== '')
          setValidationErrors((prev) => {
            return { ...prev, consecutivePeriod: '' };
          });
        break;
      case 'BREACHEDINTERVALS':
        if (
          breachingPeriodsChecksRef.current?.latestIntervalNumber?.current
            ?.value &&
          +event >=
            +breachingPeriodsChecksRef.current?.latestIntervalNumber.current
              .value &&
          validationErrors.latestInterval ==
            validationMessages.latestSmallerThanBreach
        ) {
          setValidationErrors((prev) => {
            return { ...prev, latestInterval: '' };
          });
        }
        validationMessage = getBreachingIntervalValidationMessage(event);
        setValidationErrors((prev) => {
          return { ...prev, breachedIntervals: validationMessage };
        });
        break;
      case 'LATESTINTERVAL':
        if (
          breachingPeriodsChecksRef.current?.breachNumber?.current?.value &&
          +event >=
            +breachingPeriodsChecksRef.current?.breachNumber.current.value &&
          validationErrors.breachedIntervals ==
            validationMessages.breachGreaterThanLatest
        ) {
          setValidationErrors((prev) => {
            return { ...prev, breachedIntervals: '' };
          });
        }
        validationMessage = getLatestIntervalValidationMessage(event);

        setValidationErrors((prev) => {
          return { ...prev, latestInterval: validationMessage };
        });
        break;
    }
  };

  useImperativeHandle(ref, () => ({
    consecutiveBreach: consecutiveValidationRef,
    instancesValidation: setValidationErrors,
    breachingPeriodsChecks: breachingPeriodsChecksRef,
    validationErrors,
  }));

  return (
    <div className="alarm-form-card-container">
      <div className="alarm-form-card-title-container">
        <span className="alarm-form-card-title">
          Instances Creation Criteria
        </span>
      </div>
      <div className="alarm-form-elements-container">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <ConsecutiveValidation
              alarmDefinitionDataConsecutivePeriod={
                alarmDefinitionData?.consecutive_periods ?? null
              }
              alarmMetaData={alarmMetaData}
              dataSourceMetadataForAlarm={dataSourceMetadataForAlarm}
              handleOnChange={handleOnChange}
              validationErrors={validationErrors}
              ref={consecutiveValidationRef}
            />
          </Grid>
          <BreachingPeriodsChecks
            ref={breachingPeriodsChecksRef}
            breachNumber={
              alarmDefinitionData?.execution_rules_serialized
                ?.instancesCreationPeriodCriteria?.numberOfBreachedInterval
            }
            latestIntervalNumber={
              alarmDefinitionData?.execution_rules_serialized
                ?.instancesCreationPeriodCriteria?.numberOfLatestIntervals
            }
            dataSourceMetadataForAlarm={dataSourceMetadataForAlarm}
            validationErrors={validationErrors}
            handleOnChange={handleOnChange}
          />
        </Grid>
      </div>
    </div>
  );
};

export default forwardRef<InstancesCreationCriteriaRef, Props>(
  InstancesCreationCriteria
);
